import { Injectable, inject } from '@angular/core';
import { Form, QuestionView, QuestionType } from '../models';
import { FormStore } from '../store/form.store';

/**
 * Service to persist form data to localStorage
 * Saves form state automatically and restores it on page load
 */
@Injectable({
  providedIn: 'root'
})
export class FormPersistenceService {
  private formStore = inject(FormStore);
  private readonly STORAGE_KEY = 'form-builder-current-form';
  private readonly FORM_ID_KEY = 'form-builder-current-form-id';
  private readonly SESSION_RECOVERY_KEY = 'form-builder-session-recovery';

  /**
   * Find the section ID for a question at a given index
   * Looks backwards to find the most recent Section before this position
   */
  private findSectionId(questions: QuestionView[], index: number): string | undefined {
    // Look backwards from the current position to find the most recent Section
    for (let i = index - 1; i >= 0; i--) {
      if (questions[i].questionType === QuestionType.Section) {
        return questions[i].id;
      }
    }
    return undefined;
  }

  /**
   * Recalculate section IDs for all questions based on their position relative to sections
   * This ensures Title and HelperText elements have their sectionId set correctly before saving
   */
  private recalculateSectionIds(questions: QuestionView[]): QuestionView[] {
    if (!questions || questions.length === 0) {
      return [];
    }
    
    // Sort all questions by order to ensure correct order
    const sortedQuestions = [...questions].sort((a, b) => {
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return orderA - orderB;
    });

    return sortedQuestions.map((q, index) => {
      // Section elements don't have sectionId (they are sections themselves)
      if (q.questionType === QuestionType.Section) {
        return { ...q, sectionId: undefined };
      }
      
      // Title and HelperText elements get sectionId but no questionNumber
      if (q.questionType === QuestionType.Title || q.questionType === QuestionType.HelperText) {
        const sectionId = this.findSectionId(sortedQuestions, index);
        console.log(`Persistence: ${q.questionType} element ${q.id} at index ${index}: assigning sectionId=${sectionId}`);
        return {
          ...q,
          sectionId: sectionId
        };
      }
      
      // Regular questions get sectionId
      const sectionId = this.findSectionId(sortedQuestions, index);
      return {
        ...q,
        sectionId: sectionId
      };
    });
  }

  /**
   * Save current form to localStorage
   * Ensures all question properties including sectionId are recalculated and preserved
   */
  saveForm(): void {
    const form = this.formStore.currentForm();
    if (!form) {
      return;
    }
    try {
      // Recalculate sectionIds before saving to ensure Title and HelperText have them
      const questionsWithRecalculatedSectionIds = this.recalculateSectionIds(form.questions);

      const formData = {
        id: form.id,
        title: form.title,
        description: form.description || '',
        version: form.version ?? 0,
        status: form.status,
        formType: form.formType,
        showQuestionNumbers: form.showQuestionNumbers,
        questions: questionsWithRecalculatedSectionIds,
        // An unsaved clone keeps where it came from across a refresh.
        clonedFromId: form.clonedFromId ?? null,
        updatedAt: form.updatedAt?.toISOString() || new Date().toISOString()
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(formData));
      localStorage.setItem(this.FORM_ID_KEY, form.id);
    } catch (error) {
      console.error('Error saving form to localStorage:', error);
    }
  }

  /**
   * Load form from localStorage
   */
  loadForm(): Form | null {
    try {
      const formDataStr = localStorage.getItem(this.STORAGE_KEY);
      if (!formDataStr) {
        return null;
      }

      const formData = JSON.parse(formDataStr);
      
      // Convert questions and ensure proper types
      // Preserve all properties including sectionId
      const questions = (formData.questions || []).map((q: any) => ({
        ...q,
        // Ensure dates are Date objects if they exist
        lastUpdatedOn: q.lastUpdatedOn || undefined,
        // Ensure all required fields are present
        showMenu: q.showMenu ?? false,
        isEditing: q.isEditing ?? false,
            order: q.order ?? 0,
        // Explicitly preserve sectionId if it exists
        sectionId: q.sectionId !== undefined ? q.sectionId : undefined
      }));

      const form: Form = {
        id: formData.id,
        title: formData.title,
        description: formData.description || '',
        version: formData.version ?? 0,
        status: formData.status,
        formType: formData.formType,
        showQuestionNumbers: formData.showQuestionNumbers ?? true,
        questions: questions,
        clonedFromId: formData.clonedFromId ?? null,
        updatedAt: formData.updatedAt ? new Date(formData.updatedAt) : undefined
      };

      console.log('Form loaded from localStorage with sectionIds:', questions.map((q: QuestionView) => ({
        id: q.id,
        type: q.questionType,
        sectionId: q.sectionId,
            order: q.order
      })));
      return form;
    } catch (error) {
      console.error('Error loading form from localStorage:', error);
      // Clear corrupted data
      this.clearForm();
      return null;
    }
  }

  /**
   * Check if a form exists in localStorage
   */
  hasForm(): boolean {
    return localStorage.getItem(this.STORAGE_KEY) !== null;
  }

  /**
   * Get the stored form ID
   */
  getStoredFormId(): string | null {
    return localStorage.getItem(this.FORM_ID_KEY);
  }

  /**
   * Clear form from localStorage
   */
  clearForm(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.FORM_ID_KEY);
    console.log('Form cleared from localStorage');
  }

  /**
   * Check if stored form matches current form ID
   */
  isStoredFormCurrent(formId: string): boolean {
    const storedFormId = this.getStoredFormId();
    return storedFormId === formId;
  }

  /**
   * Mark the current form for session recovery.
   * Called when a session timeout is detected (e.g., 401 error).
   * This allows the form to be recovered after the user logs back in.
   */
  markForSessionRecovery(): void {
    if (this.hasForm()) {
      const formData = localStorage.getItem(this.STORAGE_KEY);
      if (formData) {
        try {
          const form = JSON.parse(formData);
          // Only mark for recovery if form has meaningful content
          const hasContent = form.title?.trim() || (form.questions && form.questions.length > 0);
          if (hasContent) {
            localStorage.setItem(this.SESSION_RECOVERY_KEY, JSON.stringify({
              timestamp: new Date().toISOString(),
              formId: form.id,
              formTitle: form.title || 'Untitled Form'
            }));
            console.log('Form marked for session recovery:', form.title);
          }
        } catch (error) {
          console.error('Error marking form for session recovery:', error);
        }
      }
    }
  }

  /**
   * Check if there's a form awaiting session recovery.
   * Returns recovery info if available, null otherwise.
   */
  getSessionRecoveryInfo(): { timestamp: string; formId: string; formTitle: string } | null {
    try {
      const recoveryData = localStorage.getItem(this.SESSION_RECOVERY_KEY);
      if (!recoveryData) {
        return null;
      }
      const info = JSON.parse(recoveryData);
      // Also verify the form data still exists
      if (!this.hasForm()) {
        this.clearSessionRecovery();
        return null;
      }
      return info;
    } catch (error) {
      console.error('Error reading session recovery info:', error);
      this.clearSessionRecovery();
      return null;
    }
  }

  /**
   * Clear the session recovery flag.
   * Called after the user makes a choice (restore or discard).
   */
  clearSessionRecovery(): void {
    localStorage.removeItem(this.SESSION_RECOVERY_KEY);
    console.log('Session recovery flag cleared');
  }

  /**
   * Clear both the form and session recovery flag.
   * Used when user chooses to discard the recovered form.
   */
  clearFormAndRecovery(): void {
    this.clearForm();
    this.clearSessionRecovery();
  }
}

